import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["sentryUrl"];
/*
 * This implementation is based on the Navigation repo's sentryAPI.ts with simplifications:
 * https://git.hubteam.com/HubSpot/Navigation/blob/93fc2c721b79a325f59cc93d65fde282d8027343/unified-navigation-ui/static/js/utils/sentryAPI.ts
 */

import enviro from 'enviro';
import http from 'hub-http/clients/noAuthApiClient';
import { getPortalId } from 'whisper-base';

// https://github.com/getsentry/sentry-javascript/blob/a01b4ee7f7ba03167d7424daae2fb2f2206687cb/packages/raven-js/src/utils.js#L261-L301
function uuid4() {
  const crypto = window.crypto || window.msCrypto;
  if (typeof crypto !== undefined && crypto.getRandomValues) {
    // Use window.crypto API if available
    const arr = new Uint16Array(8);
    crypto.getRandomValues(arr);

    // set 4 in byte 7
    /* eslint-disable-next-line no-bitwise */
    arr[3] = arr[3] & 0xfff | 0x4000;
    // set 2 most significant bits of byte 9 to '10'
    /* eslint-disable-next-line no-bitwise */
    arr[4] = arr[4] & 0x3fff | 0x8000;
    const pad = function pad(num) {
      let v = num.toString(16);
      while (v.length < 4) {
        v = `0${v}`;
      }
      return v;
    };
    return pad(arr[0]) + pad(arr[1]) + pad(arr[2]) + pad(arr[3]) + pad(arr[4]) + pad(arr[5]) + pad(arr[6]) + pad(arr[7]);
  } else {
    // http://stackoverflow.com/questions/105034/how-to-create-a-guid-uuid-in-javascript/2117523#2117523
    return 'xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx'.replace(/[xy]/g, c => {
      /* eslint-disable-next-line no-bitwise */
      const r = Math.random() * 16 | 0;
      /* eslint-disable-next-line no-bitwise */
      const v = c === 'x' ? r : r & 0x3 | 0x8;
      return v.toString(16);
    });
  }
}

// Synced with raven-hubspot default ignoreErrors patterns plus additional whisper-specific patterns
// https://git.hubteam.com/HubSpot/raven-hubspot/blob/master/raven-hubspot/static-1.4747/js/configure.js#L63-L72
const ignoreErrors = [/Aborting: redirection in progress/, /hub-http - .+ - (0|403|404|429)/, /failed with status (0|403|404|429)/, /IDBDatabase/gi, /IndexedDB/gi, /IDBTransaction/gi, /Loading chunk .* failed/, /Aborting: notifying parents of unauthorized response/, /Cannot set property 'install' of undefined/, /ResizeObserver loop completed with undelivered notifications/, /ResizeObserver loop limit exceeded/, /'URLSearchParams' is not defined/, /Not implemented on this platform/, /Object Not Found Matching Id/i];
const getSentryData = () => {
  const hubletSuffix = enviro.ifHublet({
    isNa1: () => '',
    isNonNa1: hublet => `-${hublet}`
  });
  const envSuffix = enviro.isProd() ? '' : 'qa';
  const queryParams = `sentry_version=7&sentry_client=raven-js%2F3.19.1`;
  const sentryUrl = `https://exceptions${hubletSuffix}.hubspot${envSuffix}.com/v2/api/js/store/?${queryParams}`;
  return {
    environment: enviro.getShort(),
    level: 'error',
    platform: 'javascript',
    logger: 'javascript',
    tags: {
      portalId: getPortalId(),
      hublet: enviro.getHublet(),
      project: 'whisper-core-js'
    },
    request: {
      headers: {
        'User-Agent': navigator.userAgent
      },
      url: window.location.href
    },
    sentryUrl
  };
};

/**
 * Checks if any error in a flattened error chain matches our ignore patterns.
 * The errors array is expected to be a pre-flattened chain of errors (e.g., error -> error.cause -> error.cause.cause).
 * If any error in the chain matches any ignore pattern, the entire error chain should be ignored
 * to prevent partial error reporting that could miss important context.
 *
 * Additionally, ignores CHIRP internal errors that contain no extra information (only a message field)
 * since these errors cannot be traced back to backend issues and provide no actionable debugging context.
 *
 * @param errors Array of errors from a flattened error chain
 * @returns true if any error in the chain matches any ignore pattern, false otherwise
 */
const shouldIgnoreError = errors => {
  if (errors.length === 0 || ignoreErrors.some(errorPattern => errors.some(err => errorPattern.test(err.message)))) {
    return true;
  }
  const lastError = errors[errors.length - 1];
  if (Object.keys(lastError).length === 1 && lastError.message === 'CHIRP RPC failed with a CHIRP internal error') {
    return true;
  }
  return false;
};

/**
 * Determines if an error should be skipped from being reported to Sentry.
 * Skips reporting in two cases:
 * 1. When running from a local file (file:// protocol) to avoid noise during development
 * 2. When the error matches our ignore patterns (e.g., expected 404s, loading chunk failures)
 *
 * @param errors - Array of errors from a flattened error chain
 * @returns true if the error should be skipped, false if it should be reported
 */
const shouldSkipReporting = errors => {
  return window.location.protocol === 'file:' || shouldIgnoreError(errors);
};
let sentryDataCache;
export const captureMessage = async (message, options = {}) => {
  try {
    const error = new Error(message);
    if (shouldSkipReporting([error])) {
      return;
    }
    const _ref = sentryDataCache || getSentryData(),
      {
        sentryUrl
      } = _ref,
      sentryData = _objectWithoutPropertiesLoose(_ref, _excluded);
    await http.post(sentryUrl, {
      query: {
        deployable: sentryData.tags.project
      },
      data: Object.assign({
        message,
        event_id: uuid4(),
        timestamp: Date.now() / 1000
      }, sentryData, options, {
        tags: Object.assign({}, sentryData.tags, options.tags)
      }),
      safeMode: true
    });
  } catch (_unused) {
    // noop
  }
};

/**
 * Recursively collects essential error information from the error chain into an array.
 * Each error in the array contains only the critical fields needed for debugging:
 * - cause: The underlying error that caused this error (recursively processed)
 * - code: Error code for programmatic handling
 * - errorCode: Additional error code for specific error types
 * - errorMessage: Human-readable error description
 * - message: Standard error message
 * - status: HTTP or system status code
 * - statusText: Human-readable status description
 * - type: Error type classification
 * - correlationId: Request correlation tracking (from correlationId or __correlationId)
 * - chirpErrorType: CHIRP-specific error classification
 * - errorType: General error type classification
 *
 * @param error The error object to process
 * @param depth Current depth in the error chain (to prevent infinite recursion)
 * @param maxDepth Maximum depth to traverse (default: 3)
 * @returns Array of essential errors from the chain
 */
const collectEssentialErrors = (error, depth = 0, maxDepth = 3) => {
  if (!error || depth >= maxDepth) {
    return [];
  }
  const essentialErrorFields = ['code', 'errorCode', 'errorMessage', 'message', 'status', 'statusText', 'type', 'chirpErrorType', 'errorType', 'correlationId', '__correlationId'];
  const essentialError = {};
  for (const field of essentialErrorFields) {
    if (error[field]) {
      essentialError[field] = error[field];
    }
  }
  return [essentialError, ...collectEssentialErrors(error.cause, depth + 1, maxDepth)];
};

/**
 * Captures an error and sends it to Sentry.
 * This is a simplified version of Raven.captureException that avoids computing stack traces
 * to reduce bundle size. Instead of capturing full stack traces, it extracts essential error
 * information and sends it via captureMessage. This approach provides error tracking while
 * maintaining a smaller footprint in the compiled code.
 *
 * @param message - The message to send to Sentry.
 * @param error - The error to send to Sentry.
 * @param options - Additional options to pass to captureMessage.
 * @returns A promise that resolves when the error is sent to Sentry.
 */
export const captureError = (message, error, options) => {
  try {
    const essentialErrors = collectEssentialErrors(error);

    // Skip reporting if any error in the chain matches ignore patterns
    if (shouldIgnoreError(essentialErrors)) {
      return Promise.resolve();
    }
    return captureMessage(message, Object.assign({}, options, {
      extra: Object.assign({
        error: JSON.stringify(essentialErrors)
      }, options === null || options === void 0 ? void 0 : options.extra)
    }));
  } catch (_unused2) {
    // noop
  }
};